import React from "react";
import { ChevronDown, Loader2, X, Check, AlertTriangle, Search } from "lucide-react";
import { UseControllerProps, useController } from "react-hook-form";

interface DropdownOption {
  value: string;
  label: string;
  isDisabled?: boolean;
  /** Optional colored metadata shown to the right of the label (e.g. size / row count). */
  size?: string;
  rows?: string;
  warn?: boolean;
}

// Colored size / rows / warning shown beside an option label.
const OptionMeta: React.FC<{ option: DropdownOption }> = ({ option }) => {
  if (!option.size && !option.rows && !option.warn) return null;
  return (
    <span className="flex items-center gap-1.5 ml-2 shrink-0 text-xs">
      {option.size && (
        <span className={option.warn ? "text-amber-600 font-semibold" : "text-emerald-600 font-medium"}>
          {option.size}
        </span>
      )}
      {option.rows && <span className="text-sky-500">· {option.rows}</span>}
      {option.warn && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
    </span>
  );
};

interface DropdownProps extends UseControllerProps {
  value?: any | string[];
  options: DropdownOption[];
  placeholder?: string;
  label?: string;
  disable?: boolean;
  ref?: any;
  isLoading?: boolean;
  allowClear?: boolean;
  isMulti?: boolean;
  onSelectionChange?: (option: DropdownOption | null | DropdownOption[]) => void;
}

const CustomDropdownSelect: React.FC<DropdownProps> = ({
  options,
  placeholder = "Select an option",
  label,
  rules,
  disable = false,
  isLoading = false,
  allowClear = false,
  isMulti = false,
  onSelectionChange,
  ...props
}) => {
  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({
    ...props,
    rules,
    defaultValue: props.defaultValue || (isMulti ? [] : ""),
  });

  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm(""); // Clear search when closing
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  React.useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!isLoading && !disable) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm(""); // Clear search when opening
      }
    }
  };

  const handleSelect = (option: DropdownOption) => {
    if (option.isDisabled || isLoading || disable) return;

    if (isMulti) {
      const currentValue = Array.isArray(value) ? value : [];
      const isSelected = currentValue.includes(option.value);
      let newValue;

      if (isSelected) {
        newValue = currentValue.filter((item) => item !== option.value);
      } else {
        newValue = [...currentValue, option.value];
      }
      onChange(newValue);

      if (onSelectionChange) {
        const selectedOptions = newValue
          .map((val) => options.find((opt) => opt.value === val))
          .filter(Boolean) as DropdownOption[];
        onSelectionChange(selectedOptions);
      }
    } else {
      onChange(option.value);
      if (onSelectionChange) onSelectionChange(option);
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLoading && !disable) {
      const newValue = isMulti ? [] : "";
      onChange(newValue);
      if (onSelectionChange) onSelectionChange(isMulti ? [] : null);
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const handleRemoveTag = (e: React.MouseEvent, itemValue: string) => {
    e.stopPropagation();
    if (Array.isArray(value)) {
      onChange(value.filter((v) => v !== itemValue));
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent dropdown from closing when typing in search
    e.stopPropagation();
  };

  // Only surface the search box for longer lists — short dropdowns (provider,
  // format, yes/no) don't need it and look cleaner without.
  const showSearch = options.length > 7;

  const renderDisplay = () => {
    if (isMulti) {
      const selectedValues = Array.isArray(value) ? value : [];
      if (selectedValues.length === 0) {
        return <span className="text-gray-400">{placeholder}</span>;
      }
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          {selectedValues.map((val) => {
            const option = options.find((o) => o.value === val);
            if (!option) return null;
            return (
              <span
                key={val}
                className="bg-gray-200 text-gray-800 text-sm font-medium px-2 py-0.5 rounded-md flex items-center gap-1.5"
              >
                {option.label}
                <span
                  role="button"
                  tabIndex={0}
                  className="text-gray-500 hover:text-gray-800 cursor-pointer"
                  onClick={(e) => handleRemoveTag(e, val)}
                  onKeyDown={(e) => e.key === "Enter" && handleRemoveTag(e as any, val)}
                >
                  <X className="w-3 h-3" />
                </span>
              </span>
            );
          })}
        </div>
      );
    }

    const selectedOption = options.find((option) => option.value === value);
    return selectedOption ? (
      <span className="flex items-center min-w-0 text-gray-600">
        <span className="truncate">{selectedOption.label}</span>
        <OptionMeta option={selectedOption} />
      </span>
    ) : (
      <span className="text-gray-400">{placeholder}</span>
    );
  };

  const showClearButton = allowClear && (isMulti ? value?.length > 0 : !!value);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {rules?.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <button
        type="button"
        className={`w-full p-2.5 border rounded-md flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-gray-200 ${
          error ? "border-red-500 bg-white" : disable ? "bg-gray-100 border-gray-300 text-gray-400" : "bg-white border-gray-300"
        } ${disable || isLoading ? "cursor-not-allowed" : "cursor-default"}`}
        onClick={handleToggle}
        onBlur={onBlur}
        disabled={disable || isLoading}
      >
        <div className="flex-grow">{renderDisplay()}</div>
        <div className="flex items-center pl-2">
          {showClearButton && !isLoading && !disable && (
            <span
              role="button"
              tabIndex={0}
              className="mr-2 text-gray-500 hover:text-gray-700 cursor-pointer"
              onClick={handleClear}
              onKeyDown={(e) => e.key === "Enter" && handleClear(e as any)}
            >
              <X className="w-4 h-4" />
            </span>
          )}
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
          ) : (
            <ChevronDown
              className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "transform rotate-180" : ""}`}
            />
          )}
        </div>
      </button>
      
      {isOpen && !isLoading && !disable && (
        <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          {/* Search Input — only for longer lists */}
          {showSearch && (
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search options..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                />
              </div>
            </div>
          )}

          {/* Options List - Limited height to show ~4 options with scroll */}
          <ul className="max-h-40 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = isMulti && Array.isArray(value) && value.includes(option.value);
                return (
                  <li
                    key={option.value}
                    className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-2 ${
                      option.isDisabled
                        ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                        : isSelected
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "hover:bg-gray-100"
                    }`}
                    onClick={() => handleSelect(option)}
                  >
                    <span className="flex items-center min-w-0">
                      <span className="truncate">{option.label}</span>
                      <OptionMeta option={option} />
                    </span>
                    {isSelected && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                  </li>
                );
              })
            ) : (
              <li className="px-3 py-2 text-gray-500">
                {searchTerm ? 'No matching options found' : 'No options available'}
              </li>
            )}
          </ul>
        </div>
      )}
      {error && <div className="text-red-500 text-sm mt-1">{error.message}</div>}
    </div>
  );
};

export default CustomDropdownSelect;
